import type { Reservation, ReservationListItem } from '@stayafrica/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  ErrorState,
  NoPermissionState,
  PageHeader,
} from '@stayafrica/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ClipboardList, LogIn, LogOut, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FinanceSection } from '@/features/analytics/FinanceSection';
import { PerformanceSection } from '@/features/analytics/PerformanceSection';
import {
  PERIOD_PRESETS,
  resolvePeriod,
  todayIsoDate,
  type PeriodPreset,
  type PeriodRange,
} from '@/features/analytics/period';
import { Can, useCan } from '@/features/auth/session';
import { useSession } from '@/features/auth/session';
import { TodayList } from '@/features/dashboard/TodayList';
import { dashboardTodayQueryOptions } from '@/features/dashboard/queries';
import { CheckOutDialog } from '@/features/reservations/ReservationActionDialogs';
import {
  checkOutReservation,
  reservationKeys,
  reservationQueryOptions,
} from '@/features/reservations/queries';
import { TaskList } from '@/features/tasks/TaskList';
import { sortTasks, taskKeys, tasksQueryOptions } from '@/features/tasks/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate } from '@/lib/format';

interface DashboardSearch {
  /** Préréglage de période de la section « Performance ». */
  period?: PeriodPreset;
  /** Bornes personnalisées (`period=CUSTOM`). */
  from?: string;
  to?: string;
  /** Comparer à la période précédente. */
  compare?: boolean;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute('/_app/')({
  // La période vit dans l'URL : un tableau de bord filtré se partage et se
  // recharge à l'identique.
  validateSearch: (search: Record<string, unknown>): DashboardSearch => ({
    ...(PERIOD_PRESETS.includes(search['period'] as PeriodPreset)
      ? { period: search['period'] as PeriodPreset }
      : {}),
    ...(typeof search['from'] === 'string' && ISO_DATE.test(search['from'])
      ? { from: search['from'] }
      : {}),
    ...(typeof search['to'] === 'string' && ISO_DATE.test(search['to'])
      ? { to: search['to'] }
      : {}),
    ...(search['compare'] === true || search['compare'] === 'true' ? { compare: true } : {}),
  }),
  component: DashboardPage,
});

/**
 * Tableau de bord — bandeau « Aujourd'hui » alimenté par `GET /dashboard/today`
 * puis listes Arrivées / Départs du jour avec leurs actions rapides
 * (prototype validé `prototype/02-dashboard.html`).
 */
function DashboardPage() {
  const { t, i18n } = useTranslation([
    'dashboard',
    'reservations',
    'tasks',
    'payments',
    'cash',
    'common',
    'errors',
  ]);
  const { user } = useSession();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const canRead = useCan('reservation.read');
  // Sections analytiques : opérationnel (Performance) et financier (Finance).
  const canReadOperational = useCan('report.operational');
  const canReadFinancial = useCan('report.financial');
  const canCheckoutWithBalance = useCan('reservation.checkout.with_balance');
  // Les tuiles « Encaissé » et « Caisse » sont servies par `GET /dashboard/today`
  // (`todayCollected`, `counts.cashSessionOpen`) : aucun appel supplémentaire.
  const canReadPayments = useCan('payment.read');
  const canReadCash = useCan('cash.read');

  // Seul le check-out reste une modale : le check-in ouvre la page SCR-040.
  const [target, setTarget] = useState<{
    reservation: ReservationListItem;
    kind: 'departures';
  } | null>(null);

  const today = todayIsoDate(user.tenant.timezone);
  const preset: PeriodPreset = search.period ?? 'THIS_MONTH';
  const customFrom = search.from ?? today;
  const customTo = search.to ?? today;
  const custom: PeriodRange = { from: customFrom, to: customTo };
  const range = useMemo(
    () => resolvePeriod(preset, today, { from: customFrom, to: customTo }),
    [preset, today, customFrom, customTo],
  );
  const setSearch = (next: Partial<DashboardSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });

  const query = useQuery(dashboardTodayQueryOptions(canRead));
  // Carte « Tâches » : documents manquants, paiements attendus, options.
  const tasksQuery = useInfiniteQuery(tasksQueryOptions({ status: 'OPEN' }, true, 5));
  const detailQuery = useQuery({
    ...reservationQueryOptions(target?.reservation.id ?? ''),
    enabled: Boolean(target),
  });

  const mutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const reservation = detailQuery.data;
      if (!reservation || !target) throw new Error('missing');
      return checkOutReservation(reservation.id, { version: reservation.version, ...payload });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reservationKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: taskKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['housekeeping'] });
      setTarget(null);
    },
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const data = query.data;
  const counts = data?.counts;
  const reservation: Reservation | undefined = detailQuery.data;
  const openTasks = sortTasks(tasksQuery.data?.pages.flatMap((page) => page.data) ?? []);

  const metrics = [
    {
      key: 'arrivals',
      label: t('dashboard:metrics.arrivals'),
      value: counts?.arrivals,
      to: '/today/arrivals' as const,
    },
    {
      key: 'departures',
      label: t('dashboard:metrics.departures'),
      value: counts?.departures,
      to: '/today/departures' as const,
    },
    { key: 'inHouse', label: t('dashboard:metrics.inHouse'), value: counts?.inHouse },
    {
      key: 'incompleteCompliance',
      label: t('dashboard:metrics.incompleteCompliance'),
      value: counts?.incompleteCompliance,
      alert: true,
    },
    {
      key: 'availableResources',
      label: t('dashboard:metrics.availableResources'),
      value: counts?.availableResources,
      to: '/calendar' as const,
    },
    {
      key: 'expectedPayments',
      label: t('dashboard:metrics.expectedPayments'),
      value: data ? formatAmount(data.expectedPaymentsTotal, i18n.language) : undefined,
      small: true,
    },
    ...(canReadPayments
      ? [
          {
            key: 'collectedToday',
            label: t('dashboard:metrics.collectedToday'),
            value: data ? formatAmount(data.todayCollected, i18n.language) : undefined,
            small: true,
            to: '/billing/payments' as const,
          },
        ]
      : []),
    ...(canReadCash
      ? [
          {
            key: 'cash',
            label: t('dashboard:metrics.cash'),
            value: counts
              ? counts.cashSessionOpen
                ? t('dashboard:metrics.cashOpen')
                : t('dashboard:metrics.cashClosed')
              : undefined,
            small: true,
            // Caisse fermée : les règlements en espèces sont refusés (FL-005).
            alert: counts ? !counts.cashSessionOpen : false,
            to: '/billing/cash' as const,
          },
        ]
      : []),
  ];

  return (
    <Container>
      <PageHeader
        title={t('dashboard:greeting', { name: user.fullName.split(' ')[0] })}
        subtitle={t('dashboard:subtitle', {
          date: formatDate(new Date().toISOString(), i18n.language, user.tenant.timezone),
        })}
        actions={
          <Can permission="reservation.create">
            <Button variant="primary" asChild>
              <Link to="/reservations/new">
                <Plus className="ico" aria-hidden="true" />
                {t('reservations:create.action')}
              </Link>
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <section className="today-strip" aria-labelledby="today-heading">
          <div className="today-strip__head">
            <h2 id="today-heading">{t('dashboard:todayTitle')}</h2>
            <span className="today-strip__date">
              {formatDate(new Date().toISOString(), i18n.language, user.tenant.timezone)}
            </span>
          </div>
          <div className="today-metrics" aria-busy={query.isPending}>
            {metrics.map((metric) => {
              const content = (
                <>
                  <div className={`today-metric__v${metric.small ? ' is-sm' : ''}`}>
                    {metric.value === undefined ? (
                      <span
                        className="skeleton"
                        style={{
                          display: 'block',
                          height: 26,
                          width: 46,
                          background: 'rgba(255,255,255,.22)',
                        }}
                        aria-hidden="true"
                      />
                    ) : (
                      metric.value
                    )}
                  </div>
                  <div className="today-metric__l">{metric.label}</div>
                </>
              );
              return metric.to ? (
                <Link
                  key={metric.key}
                  to={metric.to}
                  className={`today-metric${metric.alert ? ' is-alert' : ''}`}
                >
                  {content}
                </Link>
              ) : (
                <div key={metric.key} className={`today-metric${metric.alert ? ' is-alert' : ''}`}>
                  {content}
                </div>
              );
            })}
          </div>
        </section>

        {query.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : (
          <div className="grid grid-2">
            <Card>
              <CardHeader
                title={t('dashboard:arrivalsCard')}
                icon={<LogIn className="ico ico-16" aria-hidden="true" />}
                iconTone="green"
                actions={
                  <>
                    <Badge tone="neutral">{counts?.arrivals ?? 0}</Badge>
                    <Link className="link-quiet t-13" to="/today/arrivals">
                      {t('dashboard:seeAll')}
                    </Link>
                  </>
                }
              />
              <CardBody density="flush">
                <TodayList
                  kind="arrivals"
                  rows={data?.arrivals ?? []}
                  loading={query.isPending}
                  onAction={undefined}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title={t('dashboard:departuresCard')}
                icon={<LogOut className="ico ico-16" aria-hidden="true" />}
                iconTone="blue"
                actions={
                  <>
                    <Badge tone="neutral">{counts?.departures ?? 0}</Badge>
                    <Link className="link-quiet t-13" to="/today/departures">
                      {t('dashboard:seeAll')}
                    </Link>
                  </>
                }
              />
              <CardBody density="flush">
                <TodayList
                  kind="departures"
                  rows={data?.departures ?? []}
                  loading={query.isPending}
                  onAction={(item) => setTarget({ reservation: item, kind: 'departures' })}
                />
              </CardBody>
            </Card>
          </div>
        )}

        {canReadOperational || canReadFinancial ? (
          <div className={canReadFinancial ? 'grid grid--two-thirds' : 'grid'}>
            <PerformanceSection
              preset={preset}
              onPresetChange={(value) => setSearch({ period: value })}
              range={range}
              custom={custom}
              onCustomChange={(value) => setSearch({ from: value.from, to: value.to })}
              compare={search.compare === true}
              onCompareChange={(value) => setSearch({ compare: value || undefined })}
              canRead={canReadOperational}
            />
            <FinanceSection range={range} canRead={canReadFinancial} />
          </div>
        ) : null}

        <Card>
          <CardHeader
            title={t('tasks:dashboard.title')}
            icon={<ClipboardList className="ico ico-16" aria-hidden="true" />}
            iconTone="amber"
            actions={
              <>
                <Badge tone="neutral">{openTasks.length}</Badge>
                <Link className="link-quiet t-13" to="/tasks">
                  {t('tasks:dashboard.seeAll')}
                </Link>
              </>
            }
          />
          <CardBody density="flush">
            <TaskList tasks={openTasks.slice(0, 5)} loading={tasksQuery.isPending} compact />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('dashboard:inHouseCard')}
            icon={<LogIn className="ico ico-16" aria-hidden="true" />}
            actions={<Badge tone="neutral">{counts?.inHouse ?? 0}</Badge>}
          />
          <CardBody density="flush">
            <TodayList kind="inHouse" rows={data?.inHouse ?? []} loading={query.isPending} />
          </CardBody>
        </Card>
      </div>

      {reservation && target?.kind === 'departures' ? (
        <CheckOutDialog
          open
          onOpenChange={(open) => !open && setTarget(null)}
          reservation={reservation}
          loading={mutation.isPending}
          error={mutation.error}
          canCheckoutWithBalance={canCheckoutWithBalance}
          onSubmit={(values) => mutation.mutate({ ...values })}
        />
      ) : null}
    </Container>
  );
}
